import { Box, CircularProgress, Button, useTheme } from '@mui/material'
import { IconScan } from '@tabler/icons-react'
import React, { ChangeEvent, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useDevice } from '../../../../hooks/useDevice'
import { useMenuScanFlow } from '../../../../hooks/useMenuScanFlow'
import { useAppSelector } from '../../../../store'
import {
  fetchSavedMenus,
  selectEater,
  selectSavedMenus,
} from '../../../../store/profileSlice'
import EmptyScreen from '../../../../components/EmptyScreen'
import { errorLog } from '../../../../utils/log-helper'
import { ROUTES } from '../../../../helpers/routes-helper'
import {
  SavedMenusContainer,
  MenuCardContainer,
  MenuThumbnailContainer,
  MenuPlaceholderTile,
  MenuImage,
  MenuContentContainer,
  MenuNameTypography,
  RestaurantNameTypography,
  MetaRowContainer,
  MetaText,
  TypeChip,
  DishesRowContainer,
  DishPill,
  ChevronContainer,
  ChevronIcon,
} from '../styled/ProfileSavedMenus.styled'

const ProfileSavedMenus: React.FC = (): JSX.Element => {
  const theme = useTheme()
  const { isMediumScreen, dispatch } = useAppUtilities()
  const navigate = useNavigate()
  const { isHybrid } = useDevice()
  const { startScan, uploading } = useMenuScanFlow()
  const eater = useAppSelector(selectEater)
  const savedMenusState = useAppSelector(selectSavedMenus)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!eater?.eaterId) return
    void dispatch(fetchSavedMenus({ page: 0, size: 20, sort: 'recent' }))
  }, [dispatch, eater?.eaterId])

  // Loading state
  if (savedMenusState.loading && savedMenusState.items.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  // Error state
  if (savedMenusState.error && savedMenusState.items.length === 0) {
    return (
      <Box>
        <EmptyScreen
          mainText="Unable to load saved menus"
          secondaryText={savedMenusState.error}
          imageUrl="/images/empty-screen.svg"
          containerHeight={isMediumScreen ? '40vh' : '400px'}
        >
          <Button
            onClick={() => {
              void dispatch(fetchSavedMenus({ page: 0, size: 20, sort: 'recent' }))
            }}
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            Try again
          </Button>
        </EmptyScreen>
      </Box>
    )
  }

  const handleWebFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event?.target?.files?.[0]
    event.target.value = ''
    if (!file) return
    void startScan(file).catch((e) => {
      errorLog('[ProfileSavedMenus] Scan upload failed', e)
    })
  }

  const handleScanMenuClick = async (): Promise<void> => {
    if (uploading) return
    try {
      if (isHybrid || Capacitor.isNativePlatform()) {
        const photo = await Camera.getPhoto({
          quality: 90,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Camera,
          allowEditing: false,
        })

        if (!photo.dataUrl) return

        const res = await fetch(photo.dataUrl)
        const blob = await res.blob()
        const file = new File([blob], `scan-${Date.now()}.jpg`, {
          type: blob.type || 'image/jpeg',
        })
        await startScan(file)
        return
      }
      fileInputRef.current?.click()
    } catch (e) {
      errorLog('[ProfileSavedMenus] Scan menu launch failed', e)
    }
  }

  // Empty state
  if (savedMenusState.items.length === 0) {
    return (
      <Box>
        <EmptyScreen
          mainText="No saved menus yet"
          secondaryText="Discover dishes, save your favorites, and earn points."
          imageUrl="/images/empty-screen.svg"
          containerHeight={isMediumScreen ? '40vh' : '400px'}
        >
          <Button
            onClick={() => {
              void handleScanMenuClick()
            }}
            variant="contained"
            color="secondary"
            startIcon={<IconScan size={18} />}
            sx={{ mt: 2 }}
            disabled={uploading}
          >
            {uploading ? 'Loading…' : 'Discover dishes'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleWebFileChange}
            disabled={uploading}
          />
        </EmptyScreen>
      </Box>
    )
  }

  // Helper function to format relative date
  const getRelativeDate = (dateString: string): string => {
    // If already in "X days ago" format, return as-is
    if (dateString.includes('ago') || dateString.includes('today')) {
      return dateString
    }

    // Otherwise parse and format
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Saved today'
      if (diffDays === 1) return 'Saved yesterday'
      if (diffDays < 7) return `Saved ${diffDays} days ago`
      if (diffDays < 30) return `Saved ${Math.floor(diffDays / 7)} weeks ago`
      return `Saved ${Math.floor(diffDays / 30)} months ago`
    } catch {
      return dateString
    }
  }

  // Handle menu card click
  const handleMenuClick = (menuId: string): void => {
    const selected = savedMenusState.items.find(
      (item) => (item.menuId ?? item.savedId) === menuId,
    )
    navigate(`${ROUTES.SAVED_MENU_DETAIL}/${menuId}`, {
      state: selected ? { savedMenuItem: selected } : undefined,
    })
  }

  // Render list state
  return (
    <SavedMenusContainer>
      {savedMenusState.items.map((menu) => (
        <MenuCardContainer
          key={menu.savedId}
          onClick={() => handleMenuClick(menu.menuId ?? menu.savedId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleMenuClick(menu.menuId ?? menu.savedId)
            }
          }}
        >
          {/* Thumbnail / Placeholder */}
          <MenuThumbnailContainer>
            {menu.imageUrl ? (
              <MenuImage src={menu.imageUrl} alt={menu.menuName} />
            ) : (
              <MenuPlaceholderTile>
                <IconScan size={32} color={theme.palette.primary.main} stroke={1.5} />
              </MenuPlaceholderTile>
            )}
          </MenuThumbnailContainer>

          {/* Content */}
          <MenuContentContainer>
            {/* Menu name */}
            <MenuNameTypography>{menu.menuName}</MenuNameTypography>

            {/* Restaurant name */}
            <RestaurantNameTypography>
              {menu.restaurantName ?? 'No restaurant linked'}
            </RestaurantNameTypography>

            {/* Meta row: dish count, saved date, type chip */}
            <MetaRowContainer>
              <MetaText>{menu.dishCount} dishes</MetaText>
              <MetaText>•</MetaText>
              <MetaText>{getRelativeDate(menu.savedAt)}</MetaText>
              <TypeChip
                label={menu.type === 'SCAN_ONLY' ? 'Scan Only' : 'User Provided'}
                variant="filled"
                size="small"
              />
            </MetaRowContainer>

            {/* Sample dishes */}
            {menu.sampleDishes.length > 0 && (
              <DishesRowContainer>
                {menu.sampleDishes.slice(0, 3).map((dish, idx) => (
                  <DishPill
                    key={idx}
                    label={dish}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </DishesRowContainer>
            )}
          </MenuContentContainer>

          {/* Right chevron affordance */}
          <ChevronContainer>
            <ChevronIcon />
          </ChevronContainer>
        </MenuCardContainer>
      ))}
    </SavedMenusContainer>
  )
}

export default ProfileSavedMenus
